import React, { useState, useContext, useEffect } from 'react';
import { UserContext } from '../context/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaUserCircle, FaEdit, FaSave, FaTimes, FaSpinner,
    FaChartPie, FaTasks, FaMapMarkerAlt, FaCalendarAlt,
    FaBuilding, FaPhoneAlt, FaMobileAlt, FaImage
} from 'react-icons/fa';
import styles from './Profile.module.css';
import { API } from '../config';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';

const Profile = () => {
    const user = useContext(UserContext);

    const [isEditing, setIsEditing] = useState(false);
    const [profileData, setProfileData] = useState({
        sid: '',
        full_name: '',
        email: '',
        address: '',
        phoneNumber: '',
        dateOfBirth: '',
        yearOfPassedOut: '',
        currentCompany: '',
        alternateMobile: '',
        profilePicUrl: '',
    });
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMeetings, setLoadingMeetings] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [meetings, setMeetings] = useState([]);
    const [calendarDate, setCalendarDate] = useState(new Date());

    /** 🔹 Fetch student data by email */
    useEffect(() => {
        const fetchStudentData = async () => {
            if (user && user.email) {
                try {
                    const response = await fetch(
                        `${API.GET_STUDENT_PROFILE_BY_EMAIL}?email=${user.email}`
                    );
                    
                    if (!response.ok) {
                        throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    if (data.success) {
                        const student = data.data; // ✅ correct key
                        setProfileData({
                            sid: student.id || '',
                            full_name: student.FullName || 'User',
                            email: student.EmailId || '',
                            phoneNumber: student.PhoneNumber || '',
                            address: student.Address || '',
                            dateOfBirth: student.DateOfBirth || '',
                            yearOfPassedOut: student.YearOfPassedout || '',
                            currentCompany: student.CurrentCompany || '',
                            alternateMobile: student.AlternateMobile || '',
                            profilePicUrl: student.ProfilePicUrl || '',
                        });

                    } else {
                        setMessage({ type: 'error', text: data.message || 'Failed to fetch profile data.' });
                    }
                } catch (error) {
                    console.error("Failed to fetch student data:", error);
                    setMessage({ type: 'error', text: `Failed to load profile: ${error.message}` });
                }
            }
        };

        fetchStudentData();
    }, [user]);

    /** 🔹 Fetch meetings after student data is available */
    useEffect(() => {
        const fetchUserMeetings = async () => {
            if (!profileData.email) return;

            setLoadingMeetings(true);
            try {
                const response = await fetch(
                    `${API.GET_USER_MEETINGS_ENDPOINT}?email=${profileData.email}`
                );
                
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                }
                
                const data = await response.json();
                if (data.success && Array.isArray(data.sessions)) {
                    const formattedMeetings = data.sessions.map(meeting => ({
                        id: meeting.meeting_key,
                        title: meeting.topic,
                        start: new Date(meeting.start_time),
                        end: new Date(new Date(meeting.start_time).getTime() + parseInt(meeting.duration_ms)),
                        agenda: meeting.agenda,
                        joinURL: meeting.join_link
                    }));
                    setMeetings(formattedMeetings);
                } else {
                    console.warn('Unexpected response format from meetings API:', data);
                    setMeetings([]);
                }
            } catch (error) {
                console.error("Failed to fetch user meetings:", error);
                setMeetings([]);
            } finally {
                setLoadingMeetings(false);
            }
        };

        fetchUserMeetings();
    }, [profileData.email]);

    /** 🔹 Handlers */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData({ ...profileData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePicFile(file);
            setProfileData({ ...profileData, profilePicUrl: URL.createObjectURL(file) });
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        if (!profileData.full_name.trim() || !profileData.email.trim()) {
            setMessage({ type: 'error', text: 'Full name and email cannot be empty.' });
            setLoading(false);
            return;
        }

        if (!profileData.sid) {
            setMessage({ type: 'error', text: 'Student ID is missing. Cannot save profile.' });
            setLoading(false);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('sid', profileData.sid);
            formData.append('full_name', profileData.full_name);
            formData.append('email', profileData.email);
            formData.append('phoneNumber', profileData.phoneNumber);
            formData.append('address', profileData.address);
            formData.append('dateOfBirth', profileData.dateOfBirth);
            formData.append('yearOfPassedOut', profileData.yearOfPassedOut);
            formData.append('currentCompany', profileData.currentCompany);
            formData.append('alternateMobile', profileData.alternateMobile);
            if (profilePicFile) {
                formData.append('profilePicture', profilePicFile);
            }

            const response = await fetch(API.UPDATE_STUDENT, {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();

            if (result.success) {
                setMessage({ type: 'success', text: result.message });
            } else {
                setMessage({ type: 'error', text: result.message });
            }

            setIsEditing(false);
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    /** 🔹 Calendar */
    const renderCalendarTile = ({ date, view }) => {
        if (view === 'month') {
            const isMeetingDay = meetings.some(
                (meeting) => moment(meeting.start).isSame(date, 'day')
            );
            return isMeetingDay ? <span className={styles.meetingDot}></span> : null;
        }
        return null;
    };

    const renderMeetingList = () => {
        const meetingsInView = meetings.filter(meeting =>
            moment(meeting.start).isSame(calendarDate, 'month')
        );

        if (loadingMeetings) {
            return <p className={styles.loadingListText}>Loading meetings...</p>;
        }

        if (meetingsInView.length === 0) {
            return <p className={styles.emptyListText}>No meetings scheduled this month.</p>;
        }

        return (
            <ul className={styles.meetingList}>
                {meetingsInView.map(meeting => (
                    <li key={meeting.id} className={styles.meetingItem}>
                        <div className={styles.meetingDetails}>
                            <h4 className={styles.meetingTitle}>{meeting.title}</h4>
                            <p className={styles.meetingTime}>
                                ⏰ {moment(meeting.start).format('MMM Do, h:mm A')} - {moment(meeting.end).format('h:mm A')}
                            </p>
                        </div>
                        <a
                            href={meeting.joinURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.joinButton}
                        >
                            Join
                        </a>
                    </li>
                ))}
            </ul>
        );
    };

    if (!user) {
        return (
            <div className={styles.profileContainer}>
                <p>Please log in to view your profile.</p>
            </div>
        );
    }

    return (
        <div className={styles.profilePage}>
            {/* Sidebar */}
            <motion.div
                className={styles.profileSidebar}
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                <div className={styles.sidebarHeader}>
                    <div className={styles.sidebarPic}>
                        {profileData.profilePicUrl ? (
                            <img src={profileData.profilePicUrl} alt="Profile" className={styles.profileImg} />
                        ) : (
                            <FaUserCircle size={60} color="#fff" />
                        )}
                    </div>
                    <h3 className={styles.sidebarUsername}>{profileData.full_name}</h3>
                    <p className={styles.sidebarRole}>{user.role}</p>
                </div>
                <ul className={styles.sidebarNav}>
                    <li className={styles.active}><FaUserCircle /> <span>My Profile</span></li>
                    {/* <li><FaChartPie /> <span>Progress</span></li>
                    <li><FaTasks /> <span>Assignments</span></li> */}
                </ul>
            </motion.div>

            {/* Content */}
            <motion.div
                className={styles.profileContent}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
            >
                <div className={styles.contentHeader}>
                    <h2>User Profile</h2>
                </div>

                <div className={styles.gridContainer}>
                    {/* About Me */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.cardTitle}>About Me</h3>
                            {!isEditing && (
                                <button className={styles.editBtn} onClick={() => setIsEditing(true)}>
                                    <FaEdit />
                                </button>
                            )}
                        </div>
                        <div className={styles.infoSection}>
                            <div className={styles.profilePicUpload}>
                                <span className={styles.label}>Profile Picture</span>
                                {isEditing && (
                                    <label htmlFor="profilePicInput" className={styles.uploadBtn}>
                                        <FaImage /> Upload Image
                                    </label>
                                )}
                                <input
                                    type="file"
                                    id="profilePicInput"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}>Full Name</span>
                                {isEditing ? (
                                    <input type="text" name="full_name" value={profileData.full_name} onChange={handleChange} className={styles.editInput} />
                                ) : <span className={styles.value}>{profileData.full_name}</span>}
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}>Email Address</span>
                                <span className={styles.value}>{profileData.email}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}><FaPhoneAlt /> Phone Number</span>
                                <span className={styles.value}>{profileData.phoneNumber}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}><FaMobileAlt /> Alternate Mobile</span>
                                {isEditing ? (
                                    <input type="tel" name="alternateMobile" value={profileData.alternateMobile} onChange={handleChange} className={styles.editInput} />
                                ) : <span className={styles.value}>{profileData.alternateMobile}</span>}
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}><FaMapMarkerAlt /> Address</span>
                                {isEditing ? (
                                    <textarea name="address" value={profileData.address} onChange={handleChange} className={styles.editInput} />
                                ) : <span className={styles.value}>{profileData.address}</span>}
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}><FaCalendarAlt /> Date of Birth</span>
                                {isEditing ? (
                                    <input type="date" name="dateOfBirth" value={profileData.dateOfBirth} onChange={handleChange} className={styles.editInput} />
                                ) : <span className={styles.value}>{profileData.dateOfBirth}</span>}
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}><FaCalendarAlt /> Year of Passed Out</span>
                                {isEditing ? (
                                    <input type="number" name="yearOfPassedOut" value={profileData.yearOfPassedOut} onChange={handleChange} className={styles.editInput} />
                                ) : <span className={styles.value}>{profileData.yearOfPassedOut}</span>}
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}><FaBuilding /> Current Company</span>
                                {isEditing ? (
                                    <input type="text" name="currentCompany" value={profileData.currentCompany} onChange={handleChange} className={styles.editInput} />
                                ) : <span className={styles.value}>{profileData.currentCompany}</span>}
                            </div>
                        </div>
                        <AnimatePresence>
                            {message.text && (
                                <motion.div
                                    className={`${styles.message} ${styles[message.type]}`}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                >
                                    {message.text}
                                </motion.div>
                            )}
                        </AnimatePresence>
                        {isEditing && (
                            <div className={styles.actions}>
                                <motion.button className={`${styles.actionBtn} ${styles.saveBtn}`} onClick={handleSave} disabled={loading}>
                                    {loading ? <FaSpinner className={styles.spinner} /> : <FaSave />} Save
                                </motion.button>
                                <motion.button className={`${styles.actionBtn} ${styles.cancelBtn}`} onClick={() => setIsEditing(false)}>
                                    <FaTimes /> Cancel
                                </motion.button>
                            </div>
                        )}
                    </div>

                    {/* Meetings */}
                    <div className={`${styles.card} ${styles.calendarCard}`}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.cardTitle}>Upcoming Meetings</h3>
                        </div>
                        <Calendar
                            onChange={setCalendarDate}
                            value={calendarDate}
                            tileContent={renderCalendarTile}
                        />
                        <div className={styles.meetingListWrapper}>
                            <h4>Meetings for {moment(calendarDate).format('MMMM YYYY')}</h4>
                            {renderMeetingList()}
                        </div>
                    </div>

                    {/* Progress */}
                    {/* <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.cardTitle}>My Progress</h3>
                        </div>
                        <div className={styles.progressSection}>
                            <div className={styles.progressItem}>
                                <div className={styles.progressIcon}><FaTasks /></div>
                                <div className={styles.progressInfo}>
                                    <h4>Assignments</h4>
                                    <p>15 of 20 completed</p>
                                </div>
                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBar} style={{ width: '75%' }}></div>
                                </div>
                            </div>
                            <div className={styles.progressItem}>
                                <div className={styles.progressIcon}><FaChartPie /></div>
                                <div className={styles.progressInfo}>
                                    <h4>Lessons</h4>
                                    <p>30 of 50 completed</p>
                                </div>
                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBar} style={{ width: '60%' }}></div>
                                </div>
                            </div>
                        </div>
                    </div> */}
                </div>
            </motion.div>
        </div>
    );
};

export default Profile;
